import { IconButton, Tooltip, type DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { formatDateTime, formatRelativeTime } from "@/utils/datetime";
import { TrainingSessionRefLink } from "../TrainingSessionRefLink";
import type { ShareLinkTableRow } from "./ShareLinkTableRow";
import styles from "./ShareLinksPanel.module.css";

type ShareLinkColumnsOptions = {
  nowMs: number;
  // 조직 전체를 보는 중이면 발행자 열을 표시한다. 내 링크만 볼 때는 모두 나이므로 뺀다.
  showIssuer: boolean;
  // 다시 발급 중인 행. 발급하는 동안 모든 다시 발급 버튼을 잠근다.
  reissuingId: string | null;
  onReissue: (row: ShareLinkTableRow) => void;
  onRevoke: (row: ShareLinkTableRow) => void;
};

export function createShareLinkColumns({
  nowMs,
  showIssuer,
  reissuingId,
  onReissue,
  onRevoke,
}: ShareLinkColumnsOptions): readonly DataTableColumn<ShareLinkTableRow>[] {
  const columns: DataTableColumn<ShareLinkTableRow>[] = [
    {
      key: "targetLabel",
      header: "대상",
      width: "minmax(200px, 1.6fr)",
      render: (row) => (
        <span className={styles.stack}>
          <span className={styles.primary}>{row.targetLabel}</span>
          <span className={styles.meta}>
            {row.kindLabel} · {row.filename}
          </span>
        </span>
      ),
    },
    {
      key: "sessionName",
      header: "Session",
      width: "minmax(160px, 1fr)",
      // 공유한 파일을 만든 세션을 체크포인트 탭의 그 태그 행으로 연다. Policy 표 첫 열과 같은 계약이다.
      render: (row) => (
        <TrainingSessionRefLink
          sessionId={row.sessionId}
          displayName={row.sessionName ?? row.sessionId.slice(0, 8)}
          focus={{ tab: "checkpoints", checkpoint: row.checkpointTag }}
        />
      ),
    },
  ];
  if (showIssuer) {
    columns.push({ key: "issuerName", header: "발행자", width: "120px" });
  }
  columns.push(
    { key: "sizeLabel", header: "크기", width: "96px", align: "right" },
    {
      key: "createdAtMs",
      header: "발행",
      width: "100px",
      render: (row) => (
        <Tooltip content={formatDateTime(row.createdAtMs)}>
          <span>{formatRelativeTime(row.createdAtMs, nowMs)}</span>
        </Tooltip>
      ),
    },
    {
      key: "expiresAtMs",
      header: "만료",
      width: "100px",
      render: (row) =>
        row.isExpired(nowMs) ? (
          <span className={styles.expired}>만료됨</span>
        ) : (
          <Tooltip content={formatDateTime(row.expiresAtMs)}>
            <span>{formatRelativeTime(row.expiresAtMs, nowMs)}</span>
          </Tooltip>
        ),
    },
    {
      key: "accessCount",
      header: "열람",
      width: "110px",
      align: "right",
      // 열람 수는 링크가 파일로 이동한 횟수이고 다운로드 완료 수가 아니다. hover 가 마지막 시각을 표시한다.
      render: (row) => (
        <Tooltip content={accessTooltip(row)}>
          <span className={styles.stack}>
            <span className={styles.primary}>{row.accessCount}회</span>
            <span className={styles.meta}>
              {row.lastAccessedAtMs === null
                ? "—"
                : formatRelativeTime(row.lastAccessedAtMs, nowMs)}
            </span>
          </span>
        </Tooltip>
      ),
    },
    {
      key: "uuid",
      header: "",
      width: "172px",
      align: "right",
      render: (row) => renderActions(row, reissuingId, onReissue, onRevoke),
    },
  );
  return columns;
}

function accessTooltip(row: ShareLinkTableRow): string {
  if (row.lastAccessedAtMs === null) {
    return "아직 아무도 링크를 열지 않았습니다.";
  }
  return `마지막으로 연 시각은 ${formatDateTime(row.lastAccessedAtMs)}입니다. 링크가 파일로 넘어간 횟수이며 다운로드 완료 수는 아닙니다.`;
}

// 가능한 동작은 서버가 준 canReissue/canRevoke 만 본다. FE 가 등급과 발행자를 다시 판정하지 않는다.
function renderActions(
  row: ShareLinkTableRow,
  reissuingId: string | null,
  onReissue: (row: ShareLinkTableRow) => void,
  onRevoke: (row: ShareLinkTableRow) => void,
): ReactNode {
  if (!row.canReissue && !row.canRevoke) {
    return null;
  }
  return (
    <span className={styles.rowActions}>
      {row.canReissue ? (
        <IconButton
          text="다시 발급"
          size="sm"
          variant="surface"
          title="같은 파일로 1일짜리 새 링크를 만들어 복사합니다. 기존 링크는 그대로 남습니다."
          disabled={reissuingId !== null}
          onClick={() => onReissue(row)}
        />
      ) : null}
      {row.canRevoke ? (
        <IconButton
          text="폐기"
          size="sm"
          variant="surface"
          tone="danger"
          onClick={() => onRevoke(row)}
        />
      ) : null}
    </span>
  );
}
